import React from 'react';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { CHART_COLORS, CHART_INK, formatDayLabel, formatUsdCompact, formatUsdFull } from './common';

interface DailyFlowChartProps {
  data: Array<Record<string, number | string>>;
  /**
   * Which field carries the x label. Defaults to the Financials tab's raw
   * `date` key; the Overview passes pre-formatted `label` values because its
   * buckets can be hourly.
   */
  xKey?: string;
  /** Skipped when the caller has already formatted the axis values. */
  labelFormatter?: (value: string) => string;
}

/** USD In vs Payouts Out bars with the net cash flow line on top. */
const DailyFlowChart: React.FC<DailyFlowChartProps> = ({
  data,
  xKey = 'date',
  labelFormatter = formatDayLabel,
}) => (
  <ResponsiveContainer width="100%" height="100%">
    <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
      <CartesianGrid strokeDasharray="3 3" stroke={CHART_INK.grid} vertical={false} />
      <XAxis
        dataKey={xKey}
        tickFormatter={labelFormatter}
        tick={{ fontSize: 11, fill: CHART_INK.muted }}
        tickLine={false}
        axisLine={false}
        minTickGap={16}
      />
      <YAxis
        tickFormatter={formatUsdCompact}
        tick={{ fontSize: 11, fill: CHART_INK.muted }}
        tickLine={false}
        axisLine={false}
        width={70}
      />
      <Tooltip
        labelFormatter={(label) => labelFormatter(String(label))}
        formatter={(value: number, name: string) => [formatUsdFull(value), name]}
        contentStyle={{ borderRadius: 10, border: `1px solid ${CHART_INK.grid}`, fontSize: 12 }}
      />
      <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" iconSize={8} />
      <Bar isAnimationActive={false} dataKey="usdIn" name="USD In" fill={CHART_COLORS.in} radius={[3, 3, 0, 0]} maxBarSize={36} />
      <Bar isAnimationActive={false} dataKey="payoutsOut" name="Payouts Out" fill={CHART_COLORS.out} radius={[3, 3, 0, 0]} maxBarSize={36} />
      <Line isAnimationActive={false}
        type="monotone"
        dataKey="net"
        name="Net"
        stroke={CHART_COLORS.net}
        strokeWidth={2}
        dot={false}
      />
    </ComposedChart>
  </ResponsiveContainer>
);

export default DailyFlowChart;
